<template>
  <div class="drop-container">
    <!-- Заголовок -->
    <div class="drop-header">
      <div class="header-icon">📦</div>
      <div class="header-content">
        <h1 class="header-title">Информация о доставке</h1>
        <div class="header-subtitle">Заполните данные для отправки товара</div>
      </div>
    </div>

    <!-- Кастомный инпут для загрузки файла -->
    <div 
      class="file-upload-card"
      :class="{'has-file': fileName, 'dragover': isDragging}"
      @click="triggerFileInput"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div class="file-upload-icon">
        <svg v-if="!fileName" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
        <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <path d="M9 15h6"></path>
        </svg>
      </div>
      
      <div class="file-upload-content">
        <div class="file-upload-title">
          {{ fileName || 'Прикрепите штрих-код' }}
        </div>
        <div class="file-upload-subtitle">
          {{ fileName ? 'Файл загружен' : 'Нажмите или перетащите фото' }}
        </div>
        <div class="file-upload-hint">
          Поддерживаются форматы: JPG, PNG, PDF
        </div>
      </div>
      
      <div v-if="fileName" class="file-upload-actions">
        <button class="remove-file-btn" @click.stop="removeFile">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      
      <input
        type="file"
        accept="image/*"
        ref="fileInput"
        @change="onFileChange"
        style="display: none;"
      />
    </div>

    <!-- Форма с инпутами -->
    <div class="drop-form">
      <!-- Служба доставки -->
      <div class="form-group">
        <label class="form-label">
          <span class="label-icon">🚚</span>
          <span class="label-text">Служба доставки</span>
          <span class="required-badge">Обязательно</span>
        </label>
        <div class="custom-select-wrapper">
          <select
            v-model="deliveryService"
            class="custom-select"
            :class="{'selected': deliveryService}"
          >
            <option disabled value="">Выберите службу доставки</option>
            <option value="СДЭК">СДЭК</option>
            <option value="Почта России">Почта России</option>
            <option value="Boxberry">Boxberry</option>
            <option value="Avito">Avito</option>
            <option value="Яндекс Доставка">Яндекс Доставка</option>
            <option value="5Post">5Post</option>
          </select>
          <div class="select-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
      </div>

      <!-- Трек-номер -->
      <div class="form-group">
        <label class="form-label">
          <span class="label-icon">📮</span>
          <span class="label-text">Трек-номер</span>
          <span class="required-badge">Обязательно</span>
        </label>
        <div class="input-wrapper">
          <input
            type="text"
            v-model="trackNumber"
            placeholder="Введите трек-номер"
            class="custom-input"
            enterkeyhint="done"
            @keydown.enter="hideKeyboard"
          />
          <div v-if="trackNumber" class="input-clear" @click="trackNumber = ''">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Номер заказа -->
      <div class="form-group">
        <label class="form-label">
          <span class="label-icon">🔢</span>
          <span class="label-text">Номер заказа</span>
          <span class="required-badge">Обязательно</span>
        </label>
        <div class="input-wrapper">
          <input
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            v-model="orderNumber"
            placeholder="Введите номер заказа"
            class="custom-input"
            enterkeyhint="done"
            @keydown.enter="hideKeyboard"
          />
          <div v-if="orderNumber" class="input-clear" @click="orderNumber = ''">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Ссылка на объявление -->
      <div class="form-group">
        <label class="form-label">
          <span class="label-icon">🔗</span>
          <span class="label-text">Ссылка на объявление</span>
          <span class="required-badge">Обязательно</span>
        </label>
        <div class="input-wrapper">
          <input
            type="text"
            v-model="orderLink"
            placeholder="Вставьте ссылку на объявление"
            class="custom-input"
            enterkeyhint="done"
            @keydown.enter="hideKeyboard"
          />
          <div v-if="orderLink" class="input-clear" @click="orderLink = ''">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Информационный блок -->
    <div class="info-card">
      <div class="info-header">
        <span class="info-icon">💡</span>
        <h3 class="info-title">Важная информация</h3>
      </div>
      <div class="info-content">
        <p>
          <strong>Трек-номер и номер заказа</strong> — это разные вещи. Убедитесь, что вы ввели данные верно.
          <a 
            href="https://t.me/c/2758794635/251" 
            target="_blank"
            class="info-link"
          >
            Узнать разницу
          </a>
        </p>
      </div>
    </div>

    <!-- Пустое пространство для кнопки Telegram -->
    <div class="telegram-button-space"></div>
  </div>
</template>

<script>
import api from '@/api/api';

export default {
  data() {
    return {
      fileName: '',
      isDragging: false,
      
      // Переменные для хранения данных о заказе
      deliveryService: '', 
      orderNumber: '',
      trackNumber: '',
      orderLink: '',
      
      // ID заказа из параметров маршрута
      orderId: null,
    };
  },
  props: {
    paymentAmount: {
      type: Number,
      required: false, // Изменено с true на false, так как для пре-ордер заказов это необязательно
    },
    selectedSize: {
      type: String,
      required: false, // Изменено с true на false
    },
    productName: {
      type: String,
      required: false, // Изменено с true на false
    },
    productPhoto: {
      type: String,
      required: false, // Изменено с true на false
    },
    isFromAbroad: {
      type: Boolean,
      default: false,
      required: false,
    },
    batchId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    canProceed() {
      // Для всех заказов проверяем все поля одинаково
      return this.fileName && 
             this.deliveryService && 
             this.orderNumber && 
             this.trackNumber &&
             this.orderLink;
    }
  },

  watch: {
    // Отслеживаем изменения всех полей формы
    deliveryService() {
      this.updateTelegramButton();
    },
    orderNumber() {
      this.updateTelegramButton();
    },
    trackNumber() {
      this.updateTelegramButton();
    },
    fileName() {
      this.updateTelegramButton();
    },
    orderLink() {
      this.updateTelegramButton();
    }
  },
  
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    validateAndSetFile(file) {
      this.fileName = file.name;
      this.updateTelegramButton();
    },

    removeFile() {
      this.fileName = '';
      this.$refs.fileInput.value = '';
      this.updateTelegramButton();
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },

    hideKeyboard(event) {
      event.target.blur();
    },

    showNotification(message, title = 'Уведомление') {
      const safeMessage = message || 'Операция выполнена';
      const safeTitle = title || 'Уведомление';
      
      if (window.Telegram && window.Telegram.WebApp) {
        try {
          window.Telegram.WebApp.showPopup({
            title: safeTitle,
            message: safeMessage,
            buttons: [{ type: 'close', text: 'Закрыть' }]
          });
        } catch (error) {
          console.error('Popup error:', error);
          alert(safeTitle + ': ' + safeMessage);
        }
      } else {
        alert(safeTitle + ': ' + safeMessage);
      }
    },

    // Метод для обновления состояния кнопки Telegram
    updateTelegramButton() {
      if (!window.Telegram || !window.Telegram.WebApp) return;
      
      const tg = window.Telegram.WebApp;
      
      if (this.canProceed) {
        tg.MainButton.setText('ПРОДОЛЖИТЬ');
        tg.MainButton.show();
        tg.MainButton.enable();
      } else {
        tg.MainButton.hide();
      }
    },

    // Обработка для пре-ордер заказов (isFromAbroad = true)
    async handlePreOrderSubmit() {
      console.log('📦 Начало обработки пре-ордер заказа');
      
      if (!this.canProceed) {
        this.showNotification('Заполните все обязательные поля');
        return;
      }

      const file = this.$refs.fileInput.files[0];
      if (!file) {
        this.showNotification('Файл не выбран');
        return;
      }

      let fileUrl = "";

      try {
        // Показать индикатор загрузки
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.MainButton.setText("ЗАГРУЗКА...");
          window.Telegram.WebApp.MainButton.showProgress(false);
        }

        // 1. Загружаем файл
        console.log('📤 Загрузка файла...');
        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse = await api.post("/files/upload-file/", formData);
        fileUrl = uploadResponse.data.file_url;
        
        console.log('✅ Файл загружен, URL:', fileUrl);

        // 2. Получаем ID заказа из параметров маршрута
        const targetOrderId = this.$route.params.orderId;
        
        if (!targetOrderId) {
          throw new Error('Не найден ID заказа');
        }

        console.log('📝 Отправка данных на сервер для заказа:', targetOrderId);

        // 3. Формируем данные для отправки на бэкенд
        const orderData = {
          order_id: targetOrderId,
          order_number: this.orderNumber,
          track_number: this.trackNumber,
          barcode_url: fileUrl,
          listing_url: this.orderLink
        };

        console.log('📦 Отправляемые данные:', orderData);

        // 4. Отправляем POST запрос на бэкенд
        const response = await api.post(`/drop/profile/orders/${targetOrderId}/shipping-data`, orderData);
        
        console.log('✅ Ответ от сервера:', response.data);

        if (response.data.success) {
          // 5. Показываем уведомление об успехе                         
          this.showNotification(
            'Данные для отправки успешно сохранены!',
            'Успешно'
          );

          // 6. Переходим на страницу DropProfile
          setTimeout(() => {
            this.$router.push({ 
              name: 'DropProfile',
              query: { 
                updated: 'true',
                orderId: targetOrderId 
              }
            });
          }, 1500);
          
        } else {
          throw new Error(response.data.message || 'Неизвестная ошибка при сохранении данных');
        }

      } catch (error) {
        console.error('❌ Ошибка при обработке пре-ордер заказа:', error);
        
        let errorMessage = 'Ошибка при отправке данных. Попробуйте снова.';
        
        if (error.response) {
          console.error('❌ Response error data:', error.response.data);
          errorMessage = error.response.data?.detail || 
                        error.response.data?.message || 
                        `Ошибка сервера: ${error.response.status}`;
        } else if (error.request) {
          errorMessage = 'Сервер не отвечает. Проверьте подключение к интернету.';
        } else {
          errorMessage = error.message || 'Неизвестная ошибка';
        }
        
        this.showNotification(errorMessage, 'Ошибка');
        
      } finally {
        // Сбросить состояние кнопки
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.MainButton.hideProgress();
          window.Telegram.WebApp.MainButton.setText("ПРОДОЛЖИТЬ");
        }
      }
    },

    async handleOrderSubmit() {
      console.log('📦 Начало обработки обычного заказа');
      
      if (!this.canProceed) {
        this.showNotification('Заполните все обязательные поля');
        return;
      }

      const file = this.$refs.fileInput.files[0];
      if (!file) {
        this.showNotification('Файл не выбран');
        return;
      }

      let fileUrl = "";

      try {
        // Показать индикатор загрузки
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.MainButton.setText("ЗАГРУЗКА...");
          window.Telegram.WebApp.MainButton.showProgress(false);
        }

        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post("/files/upload-file/", formData);
        fileUrl = response.data.file_url;

        // Переход на Payment
        this.$router.push({
          name: 'Payment',
          query: {
            paymentAmount: this.paymentAmount,
            selectedSize: this.selectedSize,
            productName: this.productName,
            productPhoto: this.productPhoto,
            isDrop: true, 
            deliveryService: this.deliveryService,
            orderNumber: this.orderNumber,
            trackNumber: this.trackNumber,
            baracodePhoto: fileUrl,
            orderLink: this.orderLink,
            batchId: this.batchId,
          },
        });

      } catch (error) {
        console.error("Ошибка при загрузке файла:", error);
        this.showNotification("Ошибка при загрузке файла. Попробуйте снова.", 'Ошибка');
      } finally {
        // Сбросить состояние кнопки
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.MainButton.hideProgress();
          window.Telegram.WebApp.MainButton.setText("ПРОДОЛЖИТЬ");
        }
      }
    },

    // Основной обработчик клика по кнопке Telegram
    async handleMainButtonClickDrop() {
      // Проверяем, откуда пришел пользователь - из пре-ордера или обычного заказа
      if (this.isFromAbroad) {
        // Для пре-ордер заказов нужен orderId из маршрута
        if (!this.$route.params.orderId) {
          this.showNotification('Ошибка: не найден ID заказа', 'Ошибка');
          return;
        }
        await this.handlePreOrderSubmit();
      } else {
        // Для обычных заказов проверяем наличие обязательных пропсов
        if (!this.paymentAmount || !this.selectedSize || !this.productName || !this.productPhoto) {
          this.showNotification('Ошибка: не все данные о товаре переданы', 'Ошибка');
          return;
        }
        await this.handleOrderSubmit();
      }
    }
  },

  mounted() {
    // Получаем ID заказа из параметров маршрута (для пре-ордер заказов)
    this.orderId = this.$route.params.orderId;
    
    console.log('📦 Компонент Drop загружен:', {
      orderId: this.orderId,
      isFromAbroad: this.isFromAbroad,
      paymentAmount: this.paymentAmount,
      productName: this.productName
    });
    
    if (window.Telegram && window.Telegram.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.ready();
      
      // Настроить кнопку
      tg.MainButton.setParams({
        color: '#2AABEE',
        text_color: '#FFFFFF'
      });
      
      // Привязать обработчик
      tg.MainButton.onClick(this.handleMainButtonClickDrop);
      
      // Скрыть кнопку при загрузке
      tg.MainButton.hide();
    }
  },
  
  beforeUnmount() {
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.MainButton.offClick(this.handleMainButtonClickDrop);
      window.Telegram.WebApp.MainButton.hide();
    }
  }
};
</script>

<style scoped>
.drop-container {
  padding: 16px;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding-bottom: 80px; /* Отступ для кнопки Telegram */
  border-radius: 10px;
}

/* Заголовок */
.drop-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.header-icon {
  font-size: 24px;
  background: linear-gradient(135deg, #2AABEE, #007AFF);
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-content {
  flex: 1;
}

.header-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(135deg, #fff, #ccc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}

.header-subtitle {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

/* Загрузка файла */
.file-upload-card {
  background: rgba(255, 255, 255, 0.03);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
}

.file-upload-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(42, 171, 238, 0.3);
}

.file-upload-card.dragover {
  background: rgba(42, 171, 238, 0.1);
  border-color: #2AABEE;
  transform: scale(1.02);
}

.file-upload-card.has-file {
  border-style: solid;
  border-color: #2AABEE;
  background: rgba(42, 171, 238, 0.05);
}

.file-upload-icon {
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.3s ease;
}

.file-upload-card:hover .file-upload-icon,
.file-upload-card.has-file .file-upload-icon {
  color: #2AABEE;
}

.file-upload-content {
  flex: 1;
}

.file-upload-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
  word-break: break-word;
}

.file-upload-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
}

.file-upload-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.file-upload-actions {
  position: absolute;
  top: 12px;
  right: 12px;
}

.remove-file-btn {
  background: rgba(239, 71, 111, 0.1);
  border: 1px solid rgba(239, 71, 111, 0.2);
  border-radius: 6px;
  color: #EF476F;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-file-btn:hover {
  background: rgba(239, 71, 111, 0.2);
  transform: scale(1.1);
}

/* Форма */
.drop-form {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;
}

.label-icon {
  font-size: 16px;
  opacity: 0.8;
}

.label-text {
  flex: 1;
  color: white;
}

.required-badge {
  background: rgba(239, 71, 111, 0.2);
  color: #EF476F;
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.optional-badge {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

/* Стили для select */
.custom-select-wrapper {
  position: relative;
}

.custom-select {
  width: 100%;
  height: 48px;
  padding: 0 40px 0 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 15px;
  appearance: none;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
}

.custom-select:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.custom-select:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: #2AABEE;
  box-shadow: 0 0 0 2px rgba(42, 171, 238, 0.2);
}

.custom-select.selected {
  color: white;
}

.custom-select option {
  background: #1a1a1a;
  color: white;
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

/* Стили для инпутов */
.input-wrapper {
  position: relative;
}

.custom-input {
  width: 100%;
  height: 48px;
  padding: 0 40px 0 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 15px;
  outline: none;
  transition: all 0.3s ease;
}

.custom-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.custom-input:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.custom-input:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: #2AABEE;
  box-shadow: 0 0 0 2px rgba(42, 171, 238, 0.2);
}

.input-clear {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.6;
}

.input-clear:hover {
  background: rgba(255, 255, 255, 0.12);
  opacity: 1;
  transform: translateY(-50%) scale(1.1);
}

/* Информационный блок */
.info-card {
  background: rgba(42, 171, 238, 0.1);
  border: 1px solid rgba(42, 171, 238, 0.2);
  border-radius: 16px;
  padding: 16px;
  margin-top: 20px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.info-icon {
  font-size: 18px;
  color: #2AABEE;
}

.info-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.info-content {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.info-link {
  color: #2AABEE;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.info-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

/* Пространство для кнопки Telegram */
.telegram-button-space {
  height: 60px;
}

/* Анимации */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-group {
  animation: fadeIn 0.3s ease;
}

.form-group:nth-child(1) { animation-delay: 0.1s; }
.form-group:nth-child(2) { animation-delay: 0.2s; }
.form-group:nth-child(3) { animation-delay: 0.3s; }
.form-group:nth-child(4) { animation-delay: 0.4s; }

/* Адаптивность */
@media (max-width: 360px) {
  .drop-container {
    padding: 12px;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
    font-size: 22px;
  }
  
  .header-title {
    font-size: 18px;
  }
  
  .file-upload-card {
    padding: 20px;
    min-height: 160px;
  }
  
  .custom-input,
  .custom-select {
    height: 44px;
    font-size: 14px;
  }
}

/* Улучшенный фокус для мобильных устройств */
@media (hover: none) and (pointer: coarse) {
  .custom-input:focus,
  .custom-select:focus {
    border-color: #2AABEE;
    box-shadow: 0 0 0 2px rgba(42, 171, 238, 0.2);
  }
  
  .file-upload-card:active {
    background: rgba(42, 171, 238, 0.1);
    transform: scale(0.98);
  }
}
</style>